import React, { useRef, useState, useEffect, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ImagePlus, Loader2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { programsV2Api } from '@/lib/api/v2/programs';
import config from '@/config';

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
const DEFAULT_MAX_IMAGES = 5;

function resolveImagePreviewUrl(url: string): string {
  if (!url) return url;
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('//')) return trimmed;
  if (trimmed.startsWith('/')) {
    const base = config.apiUrl?.replace(/\/$/, '') ?? '';
    return base ? `${base}${trimmed}` : trimmed;
  }
  return trimmed;
}

export interface ProgramTypeHeroImageUploadProps {
  imageUrls: string[];
  onChange: (imageUrls: string[]) => void;
  disabled?: boolean;
  uploadFile?: (file: File) => Promise<string>;
  maxImages?: number;
  label?: string;
  inputId?: string;
  helperText?: string;
}

export const ProgramTypeHeroImageUpload: React.FC<ProgramTypeHeroImageUploadProps> = ({
  imageUrls,
  onChange,
  disabled = false,
  uploadFile = programsV2Api.uploadProgramTypeImage,
  maxImages = DEFAULT_MAX_IMAGES,
  label = 'Hero images',
  inputId = 'hero-images-upload',
  helperText,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [displayUrls, setDisplayUrls] = useState<string[]>(() => imageUrls ?? []);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [imageLoadErrors, setImageLoadErrors] = useState<Record<number, boolean>>({});

  const normalizedProp = useMemo(() => imageUrls ?? [], [imageUrls]);

  useEffect(() => {
    setDisplayUrls(normalizedProp);
    setImageLoadErrors({});
    setCarouselIndex((i) =>
      i >= normalizedProp.length ? Math.max(0, normalizedProp.length - 1) : i,
    );
  }, [normalizedProp]);

  const previewSrcs = useMemo(
    () => displayUrls.map((url) => resolveImagePreviewUrl(url)),
    [displayUrls],
  );

  const currentPreviewSrc = previewSrcs[carouselIndex] ?? '';

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (files.length === 0) return;

    setError(null);

    const valid: File[] = [];
    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError('Invalid file type. Allowed: JPEG, JPG, PNG, WebP.');
        return;
      }
      if (file.size > MAX_SIZE_BYTES) {
        setError(`File too large: ${file.name}. Max size is ${MAX_SIZE_MB} MB.`);
        return;
      }
      valid.push(file);
    }

    const remainingSlots = maxImages - displayUrls.length;
    if (remainingSlots <= 0 && maxImages > 1) {
      setError(`Maximum ${maxImages} images allowed. Remove one to add more.`);
      return;
    }
    const toUpload = valid.slice(
      0,
      Math.max(remainingSlots, maxImages === 1 ? 1 : remainingSlots),
    );
    if (toUpload.length < valid.length && remainingSlots > 0) {
      setError(
        `Only ${remainingSlots} slot(s) left. Uploading first ${toUpload.length} of ${valid.length} selected.`,
      );
    }

    setIsUploading(true);
    const collected: string[] = maxImages === 1 ? [] : [...displayUrls];

    try {
      for (let i = 0; i < toUpload.length; i++) {
        setUploadProgress({ current: i + 1, total: toUpload.length });
        const url = await uploadFile(toUpload[i]);
        if (maxImages === 1) {
          collected.length = 0;
          collected.push(url);
        } else {
          collected.push(url);
        }
        flushSync(() => {
          setDisplayUrls([...collected]);
        });
        onChange([...collected]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  const handleRemove = (index: number) => {
    const next = displayUrls.filter((_, i) => i !== index);
    setDisplayUrls(next);
    setImageLoadErrors((prev) => {
      const out: Record<number, boolean> = {};
      Object.entries(prev).forEach(([k, v]) => {
        const ki = Number(k);
        if (ki < index) out[ki] = v;
        else if (ki > index) out[ki - 1] = v;
      });
      return out;
    });
    setCarouselIndex((i) => {
      if (i >= next.length) return Math.max(0, next.length - 1);
      if (i > index) return i - 1;
      return i;
    });
    onChange(next);
  };

  const hasImages = displayUrls.length > 0;
  const atMaxImages = displayUrls.length >= maxImages;
  const allowMultiple = maxImages > 1;
  const pickerDisabled = disabled || isUploading || (atMaxImages && allowMultiple);

  const defaultHelper =
    helperText ??
    (allowMultiple
      ? `Allowed: JPEG, JPG, PNG, WebP. Max ${MAX_SIZE_MB} MB per image. Max ${maxImages} images. You can select multiple files.`
      : `Allowed: JPEG, JPG, PNG, WebP. Max ${MAX_SIZE_MB} MB. Square 1:1 recommended (min 200×200px).`);

  return (
    <div className="space-y-3">
      <Label className="text-base font-semibold text-gray-900">{label}</Label>

      {hasImages ? (
        <div className="space-y-2">
          <div className="relative inline-block w-full max-w-md overflow-hidden rounded-lg border bg-muted/30">
            <div className="relative flex aspect-video min-h-[200px] items-center justify-center bg-muted/50">
              <img
                key={currentPreviewSrc || carouselIndex}
                src={currentPreviewSrc}
                alt={`${label} ${carouselIndex + 1}`}
                className="max-h-[240px] w-auto object-contain"
                onLoad={() => setImageLoadErrors((p) => ({ ...p, [carouselIndex]: false }))}
                onError={() => setImageLoadErrors((p) => ({ ...p, [carouselIndex]: true }))}
              />
              {imageLoadErrors[carouselIndex] && (
                <div className="absolute inset-0 flex items-center justify-center bg-muted/80 p-2 text-center text-sm text-muted-foreground">
                  Image could not be loaded.
                </div>
              )}
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full shadow"
                onClick={() => handleRemove(carouselIndex)}
                disabled={disabled || isUploading}
                aria-label="Remove image"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {displayUrls.length > 1 && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute left-2 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full shadow"
                  onClick={() =>
                    setCarouselIndex((i) => (i <= 0 ? displayUrls.length - 1 : i - 1))
                  }
                  disabled={disabled}
                  aria-label="Previous"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute right-2 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full shadow"
                  onClick={() =>
                    setCarouselIndex((i) => (i >= displayUrls.length - 1 ? 0 : i + 1))
                  }
                  disabled={disabled}
                  aria-label="Next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1">
                  {displayUrls.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setCarouselIndex(i)}
                      className={`h-2 rounded-full transition-colors ${
                        i === carouselIndex ? 'bg-primary w-4' : 'bg-muted-foreground/40 w-2'
                      }`}
                      aria-label={`Go to image ${i + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          {allowMultiple ? (
            <p className="text-sm text-muted-foreground">
              {displayUrls.length} image(s). Use arrows to browse; X to remove current. Add more
              below.
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          multiple={allowMultiple}
          onChange={handleFileChange}
          disabled={pickerDisabled}
          className="sr-only"
          id={inputId}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={pickerDisabled}
          className="gap-2"
        >
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {uploadProgress
                ? `Uploading ${uploadProgress.current} of ${uploadProgress.total}…`
                : 'Uploading…'}
            </>
          ) : (
            <>
              <ImagePlus className="h-4 w-4" />
              {hasImages
                ? allowMultiple
                  ? 'Add more images'
                  : 'Replace image'
                : allowMultiple
                  ? 'Choose images'
                  : 'Choose image'}
            </>
          )}
        </Button>
      </div>

      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <p className="text-sm text-muted-foreground">{defaultHelper}</p>
    </div>
  );
};
